import { z } from 'zod'

// === Page Types ===

export const notionPageItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  icon: z.string().nullable(),
  type: z.enum(['page', 'database']),
  parent_id: z.string().nullable(),
  last_edited_time: z.string().datetime(),
  has_children: z.boolean(),
})

export type NotionPageItem = z.infer<typeof notionPageItemSchema>

export const notionPageListResponseSchema = z.object({
  pages: z.array(notionPageItemSchema),
  has_more: z.boolean(),
  next_cursor: z.string().nullable(),
})

export type NotionPageListResponse = z.infer<typeof notionPageListResponseSchema>

export const notionPageChildrenResponseSchema = z.object({
  pages: z.array(notionPageItemSchema),
  parent_id: z.string(),
  parent_title: z.string(),
  has_more: z.boolean(),
  next_cursor: z.string().nullable(),
})

export type NotionPageChildrenResponse = z.infer<typeof notionPageChildrenResponseSchema>

// === Sync Configuration ===

export const syncFrequencySchema = z.enum(['manual', 'daily', 'weekly'])

export type SyncFrequency = z.infer<typeof syncFrequencySchema>

export const syncStatusSchema = z.enum(['idle', 'pending', 'syncing', 'error'])

export type SyncStatus = z.infer<typeof syncStatusSchema>

export const notionSyncConfigRequestSchema = z.object({
  enabled: z.boolean().default(true),
  page_ids: z.array(z.string()).min(1),
  frequency: syncFrequencySchema.default('manual'),
  include_subpages: z.boolean().default(true),
})

export type NotionSyncConfigRequest = z.infer<typeof notionSyncConfigRequestSchema>

export const notionSyncConfigResponseSchema = z.object({
  enabled: z.boolean(),
  page_ids: z.array(z.string()),
  page_titles: z.array(z.string()),
  frequency: z.string(),
  include_subpages: z.boolean(),
  last_sync_at: z.string().datetime().nullable(),
  sync_status: z.string(),
})

export type NotionSyncConfigResponse = z.infer<typeof notionSyncConfigResponseSchema>

export const notionSyncTriggerResponseSchema = z.object({
  message: z.string(),
  status: z.string(),
  task_id: z.string().nullable(),
})

export type NotionSyncTriggerResponse = z.infer<typeof notionSyncTriggerResponseSchema>

// === UI Options ===

export const SYNC_FREQUENCY_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'daily', label: 'Diariamente' },
  { value: 'weekly', label: 'Semanalmente' },
] as const

// === Helper Functions ===

export function formatSyncDate(dateString: string | null): string {
  if (!dateString) return 'Nunca sincronizado'
  const date = new Date(dateString)
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function getSyncStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    idle: 'Disponivel',
    pending: 'Aguardando',
    syncing: 'Sincronizando',
    error: 'Erro',
  }
  return labels[status] || status
}

export function getSyncStatusColor(status: string): 'default' | 'secondary' | 'destructive' {
  const colors: Record<string, 'default' | 'secondary' | 'destructive'> = {
    idle: 'secondary',
    pending: 'default',
    syncing: 'default',
    error: 'destructive',
  }
  return colors[status] || 'secondary'
}
